import React from 'react';
import { LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { AppIcons, AppIconName } from '../../config/AppIcons';

interface SidebarItemProps {
    icon?: LucideIcon;
    iconName?: AppIconName;
    label: string;
    to: string;
    disabled?: boolean;
    collapsed: boolean;
    badge?: string | number;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
    icon,
    iconName,
    label,
    to,
    disabled,
    collapsed,
    badge,
    onClick
}) => {
    const Icon = icon || (iconName ? AppIcons[iconName] : null);

    if (!Icon) {
        return null;
    }

    if (disabled) {
        return (
            <div
                className={clsx(
                    'flex items-center px-3 py-2 text-gray-400 cursor-not-allowed transition-colors rounded-lg',
                    collapsed ? 'justify-center' : ''
                )}
                title={collapsed ? label : undefined}
            >
                <Icon size={20} className={clsx(collapsed ? '' : 'mr-3')} />
                {!collapsed && <span className="text-sm font-medium">{label}</span>}
            </div>
        );
    }

    return (
        <NavLink
            to={to}
            onClick={onClick}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
                clsx(
                    'flex items-center px-3 py-2 transition-colors rounded-lg group text-gray-700 hover:bg-gray-100 hover:text-gray-900',
                    (isActive && to !== '#') ? 'bg-primary-50 text-primary-700 font-semibold' : '',
                    collapsed ? 'justify-center' : ''
                )
            }
        >
            <div className="flex items-center flex-1">
                <Icon size={20} className={clsx(collapsed ? '' : 'mr-3')} />
                {!collapsed && <span className="text-sm">{label}</span>}
            </div>
            {!collapsed && badge && (
                <span
                    className={clsx(
                        'text-xs font-bold px-2 py-0.5 rounded-full',
                        typeof badge === 'number' && badge > 0
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-200 text-gray-700'
                    )}
                >
                    {badge}
                </span>
            )}
        </NavLink>
    );
};

export default SidebarItem;
